/*
 * @Author: Q in
 * @Date: 2026-03-27 19:22:06
 * @LastEditors: Q in 
 * @LastEditTime: 2026-07-27 17:07:32
 * @FilePath: /new_kefu_pc/src/mixins/admin-socket-chat.js
 * @Description: 客服端聊天全局共享状态与方法
 *
 * 使用方式：
 *   import globalMixin from '@/mixins/admin-socket-chat.js';
 *   // 读取状态
 *   globalMixin.state.currentSession
 *   // 调用方法
 *   globalMixin.methods.generateMessageId()
 *   // 使用静态 UI 配置
 *   globalMixin.ui.emojiCategories
 */
import Vue from 'vue';
import { playNotificationSound } from '@/utils/audio';
import { formatTime } from '@/utils/publicFn.js';
import browserNotification from '@/utils/browserNotification';

/** 消息类型 */
const MESSAGE_TYPES = {
	0: '富文本',
	1: '图片',
	2: '文件',
	3: '系统消息',
	4: '商品卡片',
	5: '订单卡片',
	6: '视频',
	7: '音频',
	8: '咨询卡片',
	9: '来源商品卡片 - PUBG',
	10: '@用户消息',
	11: '催一催',
	12: '撤回消息',
    13: '三角洲-商品卡片',
	14: '三角洲-订单卡片',
	15: '三角洲-撞车卡片'
};

// ─── 静态 UI 配置（不需要响应式，抽离出 globalState）────────────────────────
export const ui = {
	/** 表情列表 */
	emojiCategories: ['😀', '😂', '😍', '😘', '😢', '😭', '😔', '😞', '😡', '😠', '😤', '🤬', '😱', '😨', '😰', '😳', '😜', '😝', '😛', '😎', '🤔', '😕', '😶', '🤐', '🤗', '🥰', '💖', '❤️', '🥳', '🎉', '🎊', '🎶', '🤑', '💰', '💸', '🥺', '🙈', '🙉', '🙊'],

	/** 客服在线状态选项 */
	kefuStatus: [
		{ id: 3, class: 'online', name: '在线' },
		{ id: 1, class: 'pending', name: '繁忙' },
		{ id: 2, class: 'offline', name: '离开' }
	],

	/** 右侧面板标签 */
	tags: [{ id: 1, name: '买家信息' }],

	/** 用户相关记录 tab */
	relatedRecordsTab: [
		{ id: 1, name: '咨询宝贝' },
		{ id: 2, name: '最近订单' },
		{ id: 3, name: '足迹' }
	],

	/** 沟通状态 */
	communicationStatus: [
		{ id: 1, name: '初步沟通' },
		{ id: 2, name: '重复来访' }
	]
};

// ─── 响应式全局状态（运行时动态数据）────────────────────────────────────────

export const globalState = Vue.observable({
	// ── WebSocket & DOM 引用 ──────────────────────────────────────────────
	/** 客服端 WebSocket 实例 */
	adminSocket: null,
	wsConnected: false,
	chatMessages: null,
	editorRef: null,
	showChat: false,

	// ── 用户信息 ──────────────────────────────────────────────────────────
	/** 当前登录的客服信息 */
	kefuUserInfo: {},
	/** 接单统计 */
	reception_stats: {},

	// ── 会话列表 ──────────────────────────────────────────────────────────
	// 搜索框
	searchValue: '',
	// 用户列表标签页
	tabs: [
		{ id: -1, name: '正在接待' },
		{ id: 0, name: '全部买家' },
		{ id: 1, name: '群聊' }
	],
	activeTab: -1,
	/**
	 * 会话列表（四个维度）
	 * all_disturb:     全部（未设置免打扰）
	 * all_not_disturb: 全部（已设置免打扰）
	 * group:           群聊
	 * private:         私聊
	 */
	sessionList: {
		all_disturb: [[], []],
		all_not_disturb: [],
		group: [],
		private: []
	},
    // 全部买家会话意外记录数
    buyer_session_unexpected_records: 0,
    // 群聊会话意外记录数
    group_session_unexpected_records: 0,
    // 全部买家列表参数
    buyerSessionListParams: {
        loading: false,
		page: 1,
		page_size: 40,
        total: 0
	},
    // 群聊列表参数
    groupSessionListParams: {
        loading: false,
		page: 1,
		page_size: 40,
        total: 0
	},

	// ── 当前会话 ──────────────────────────────────────────────────────────
	/** 当前选中的会话对象 */
	currentSession: {},
	/** 当前选中的会话信息 */
	currentSessionInfo: {},
	/** 商品列表 */
	productParams: {
		checked_list: [],
		checked_count: 0,
		checked_price: 0,
		page: 1,
		page_size: 20,
		total: 0
	},
	productList: [],

	// ── 消息列表 ──────────────────────────────────────────────────────────
	/** 当前会话的消息列表 */
	sessionMessagesList: [],
	/** 是否正在加载历史消息 */
	loadingMessages: false,
	/**
	 * 分页参数
	 * page:      当前页码
	 * next_page: 下一页页码，'done' 表示没有更多
	 */
	pageParams: {
		page: 1,
		next_page: 'done'
	},
	/**
	 * 意外记录数：当前会话中用户和客服发送的消息总数
	 * 用于加载更多时告知服务端需要跳过的条数，避免重复
	 */
	unexpected_records: 0,

	// ── 其他业务状态 ──────────────────────────────────────────────────────
	/** 建群时在线客服列表 */
	csrList: [],
	/** 快捷回复列表 */
	admin_fast_reply: {},
	/** 输入框 HTML 内容（含图片） */
	editorHtmlContent: '',
	/** 输入框纯文本内容 */
	editorTextContent: '',
	/** 右侧面板当前选中 tab */
	activeRelatedTab: 1
});

// ─── 全局方法 ────────────────────────────────────────────────────────────────

export const globalMethods = {
	/**
	 * 服务端初始化消息：写入四个维度的会话列表
	 * 各列表 reverse() 使最新会话排在前面
	 */
	initialize(data) {
		const that = globalState;
		const date = data.data;
		// console.log('initialize', date);
		that.reception_stats = date.reception_stats || {};
		that.kefuUserInfo = date.user_info || {};

		// 置顶的会话列表
		that.sessionList.all_disturb[0] = date.session.all_disturb.filter((item) => item.is_top == 1).reverse();
		// 未置顶的会话列表
		that.sessionList.all_disturb[1] = date.session.all_disturb.filter((item) => item.is_top == 0).reverse();
		
		that.sessionList.all_not_disturb = [...date.session.all_not_disturb].reverse();
		that.sessionList.group = [...date.session.group].reverse();
		that.sessionList.private = [...date.session.private].reverse();
	},

	show_msg(data) {
	    if (data.code == 0) this.$message.warning(data.msg);
	},

    // 创建订单群聊完成 - 给所有群成员发送新群聊消息
    new_group_created(data) {
        const that = globalState;
        const date = data.data;
        console.log('创建新群聊完成消息1', date);
        that.sessionList.all_disturb[1].unshift(date);
        that.sessionList.group.unshift(date);
    },

	// 搜索会话列表
	searchSessionList(tab) {
		const that = globalState;
        
		// 获取商品列表
		that.adminSocket.send({
			a: 'searchSession',
			c: 'Message',
			data: {
				session_type: tab,
				keywords: that.searchValue
			}
		});

        if (localStorage.getItem('order_sn')) localStorage.removeItem('order_sn');
	},

	// 搜索会话列表成功
	search_session(data) {
		const that = globalState;
		const date = data.data;
		// console.log('search_session', date);
		if (date.session_type == '-1') {
			// 全部
			// 置顶的会话列表
			that.sessionList.all_disturb[0] = date.all_disturb.filter((item) => item.is_top == 1).reverse();
			// 未置顶的会话列表
			that.sessionList.all_disturb[1] = date.all_disturb.filter((item) => item.is_top == 0).reverse();
			// 已折叠会话列表
			that.sessionList.all_not_disturb = [...date.all_not_disturb].reverse();
		} else if (date.session_type == '0') {
			// 私聊
			that.sessionList.private = [...date.private].reverse();
		} else {
			// 群聊
			that.sessionList.group = [...date.group].reverse();
		}
	},

	// 客服端状态变更消息：更新当前客服状态
	csr_change_status(data) {
		const that = globalState;
		const date = data.data;
		// console.log('csr_change_status', date);
		if (that.kefuUserInfo.user_id == date.csr) {
			that.kefuUserInfo.status = date.csr_status;
		}
	},

	// 商品列表
	product_list(data) {
		const that = globalState;
		const date = data.data;
		// console.log('product_list', date);
		that.productParams.total = date.total;
		date.list.forEach((item) => (item.checked = false));

		if (that.productParams.checked_list.length > 0) {
			date.list.forEach((item) => {
				item.checked = that.productParams.checked_list.some((i) => i.id == item.id);
			});
		}
		that.productList = date.list || [];
	},

	/**
	 * 服务端返回聊天记录：
	 * - 第一页：直接替换消息列表并滚到底部
	 * - 加载更多：前插消息并恢复滚动位置（避免跳顶）
	 */
	chat_record(data) {
		const that = globalState;
		const date = data.data;
		// console.log('chat_record', date);
		// 是否是第一页加载
		const isFirstLoad = date.page == 1;

		// 加载更多时，先记录当前滚动高度，插入后用差值恢复位置
		const prevScrollHeight = that.chatMessages ? that.chatMessages.scrollHeight : 0;

		// 设置分页、新消息、添加撤回状态
		that.pageParams = { page: date.page, next_page: date.next_page };
		const newMessages = [...date.chat_record[0].data].reverse();
		newMessages.forEach((item) => (item.isrevoke = false));

		// 处理消息列表
		if (isFirstLoad) {
			that.sessionMessagesList = newMessages;
			that.unexpected_records = 0;
		} else {
			that.sessionMessagesList = [...newMessages, ...that.sessionMessagesList];
		}
		that.loadingMessages = false;

		this.$nextTick(() => {
			const el = that.chatMessages;
			if (!el) return;
			el.scrollTop = isFirstLoad ? el.scrollHeight : el.scrollHeight - prevScrollHeight;
		});
	},

	/**
	 * 发送消息成功回调
	 * 用真实 id 替换本地临时 id
	 */
	send_message(data) {
		const that = globalState;
		const date = data.data;
		// console.log('send_message', date);
		// 更新消息id
		that.sessionMessagesList = that.sessionMessagesList.map((item) => (item.id == date.message_id ? { ...item, id: Number(date.id) } : item));
		that.unexpected_records = that.unexpected_records + 1;
	},

	/**
	 * 构建本地消息并追加到消息列表，同时更新会话列表中的最新消息
	 * message_type: 0=富文本 1=图片 6=视频（其他类型由服务端推送）
	 * @param {Object} data       - { message, message_type }
	 * @param {string} fun        - 'append'（目前只支持追加）
	 * @param {number} message_id - 临时消息 ID（时间戳，服务端确认后替换为真实 id）
	 */
	buildRecord(data, fun = 'append', message_id = 'none') {
		const that = globalState;

		// 构建消息对象（所有类型结构相同）
		const message = {
			avatar: that.kefuUserInfo.avatar || '',
			createtime: message_id,
			createtime_text: formatTime(message_id, 'YYYY-MM-DD HH:mm:ss'),
			id: message_id,
			message: data.message,
			message_type: data.message_type,
			name: that.kefuUserInfo.nickname || '',
			sender: 'me', // 'me'=客服自己发的
			sender_id: that.kefuUserInfo.id || '',
			sender_identity: 0, // 0=客服，1=用户
			session_id: that.currentSession.id || '',
			isrevoke: false, // 是否显示撤回弹窗
			status: 0 // 0=未读
		};

		if (fun === 'append') {
			that.sessionMessagesList = [...that.sessionMessagesList, message];
		}

		// 消息类型 → 会话列表展示文本映射
		const msgTextMap = {
			0: data.message, // 富文本：显示原文
			1: '[图片]',
			2: '[文件]',
			3: data.message, // 系统消息：显示原文
			4: '[商品卡片]',
			5: '[订单卡片]',
			6: '[视频]',
			7: '[音频]',
			8: '[咨询卡片]',
			9: '[来源商品卡片]',
			10: data.message,
			11: '催一催',
			12: '撤回消息'
		};

		// 更新会话列表中对应会话的最新消息和时间
		const sessionId = that.currentSession.id;
		const lastTime = formatTime(message_id, 'HH:mm');
		const updateList = (list) => list.map((item) => (item.id == sessionId ? { ...item, message_type: data.message_type, last_message: msgTextMap[data.message_type] || '', last_time: lastTime, last_timestamp: message_id } : item));

		if (that.currentSession.session_type == 0) {
			// 私聊：更新私聊列表
			that.sessionList.private = updateList(that.sessionList.private);
		} else {
			// 群聊：更新群聊列表和已设置免打扰列表
			that.sessionList.group = updateList(that.sessionList.group);
			that.sessionList.all_not_disturb = updateList(that.sessionList.all_not_disturb);
		}
		// 私聊：更新私聊列表
		if (that.currentSession.is_top == 1) {
			that.sessionList.all_disturb[0] = updateList(that.sessionList.all_disturb[0]);
		} else {
			that.sessionList.all_disturb[1] = updateList(that.sessionList.all_disturb[1]);
		}

		// 发送后自动滚到底部
		this.$nextTick(() => {
			const el = that.chatMessages;
			if (el) el.scrollTop = el.scrollHeight;
		});
	},

	// 已读消息成功回调
	read_message_done(data) {
		const that = globalState;
		const date = data.data;
		// console.log('read_message_done', date);
		if (date.session_id != that.currentSession.id) return;
		if (date.record_id == 'all') {
			that.sessionMessagesList = that.sessionMessagesList.map((item) => (item.status == 0 ? { ...item, status: 1 } : item));
		} else {
			that.sessionMessagesList = that.sessionMessagesList.map((item) => (item.id == date.record_id ? { ...item, status: 1 } : item));
		}
	},

	// 撤回消息成功回调
	recall_message(data) {
		const that = globalState;
		const date = data.data;
		// console.log('recall_message', date);
		// record_id :  "3037"
		// session_id : "442"
		if (date.session_id == that.currentSession.id) {
			// 更新当前会话列表中的消息
			that.sessionMessagesList = that.sessionMessagesList.map((item) => (item.id == date.record_id ? { ...item, message_type: 12, isrevoke: false, message: item.name + ' 撤回了一条消息。' } : item));
		}
		// 更新左侧列表中的最新消息
		this._updateSessionListForRecall(date);
	},

	// ── 撤回消息成功工具方法 ──────────────────────────────────────────────────────────
	/**
	 * 更新撤回消息后的会话列表
	 * @param {Object} date - 撤回消息数据
	 * @private
	 */
	_updateSessionListForRecall(date) {
		const that = globalState;
		const updateList = (list) =>
			list.map((item) =>
				item.id == date.session_id
					? {
							...item,
							last_message: date.last_message || ''
					  }
					: item
			);

		if (that.currentSession.session_type == 0) {
			// 私聊：更新私聊列表
			that.sessionList.private = updateList(that.sessionList.private);
		} else {
			// 群聊：更新群聊列表
			that.sessionList.group = updateList(that.sessionList.group);
			that.sessionList.all_not_disturb = updateList(that.sessionList.all_not_disturb);
		}
		// 全部（未设置免打扰）列表始终更新
		that.sessionList.all_disturb[0] = updateList(that.sessionList.all_disturb[0]);
		that.sessionList.all_disturb[1] = updateList(that.sessionList.all_disturb[1]);
	},

	/**
	 * 收到新消息：
	 * - 弹窗未显示时：推送桌面通知
	 * - 非当前会话：更新会话列表中的未读数和最新消息
	 * - 当前会话：追加消息到列表，并判断是否自动滚到底部
	 */
	new_message(data) {
		const that = globalState;
		const date = data.data;
		// console.log('new_message', date);

		/**
		 * 1、先判断当前会话是私聊还是群聊
		 * 2、在判断当前会话有没有设置置顶（设置置顶了就更新 globalMixin.state.sessionList.all_disturb[0]）
		 *    没有设置置顶更新 globalMixin.state.sessionList.all_disturb[1]
		 */
		// 播放提示音
		playNotificationSound();
		// 弹窗未显示时推送桌面通知
		if (!this.showChat) {
			this._showNotification(date);
		}

        if (!browserNotification.isPageVisible()) {
            this._handleEventNotify(date);
        }
		// 是否是当前会话
		const isCurrentSession = that.currentSession.id && that.currentSession.id == date.session_id;

		// 当前会话：追加消息到列表
		if (isCurrentSession) {
			// 记录当前会话框 用户发送消息数量 + 和客服发送消息数量
			that.unexpected_records = that.unexpected_records + 1;
			// 当前会话：追加消息到列表
			this._appendMessageToList(date);
			// 更新会话列表中的未读数和最新消息
			this._updateCurrentSessionList(date);
			// 发送已读消息请求
			this._sendReadMessageRequest(date);
			// 用户在底部时才自动滚动，避免打断用户查看历史消息
			this._autoScrollToBottom();
		} else {
			this._updateSessionList(date);
		}
	},
	// ── 操作消息工具方法 ──────────────────────────────────────────────────────────
    // 浏览器事件通知
    _handleEventNotify(date) {
        const that = globalState;
        browserNotification.showNotification({
            title: '新消息',
            body: date.last_message,
            timeout: 10000,
            onClick: (notif) => {
                window.focus();
                that.showChat = true;
                notif.close();
            },
        });
    },
    
	// 弹窗未打开的消息桌面通知
	_showNotification(date) {
		const that = globalState;
		this.$messageNotification({
			title: '新消息',
			name: date.nickname,
			message: date.last_message,
			avatar: date.avatar,
			duration: 5000,
			showProgress: true,
			onClick: () => {
				that.showChat = true;
				if (that.currentSession.id == date.session_id) return;
				let sessionType = date.session_type == 0 ? 'private' : 'group';
				that.currentSession = that.sessionList[sessionType].find((item) => item.id == date.session_id);

				this._updateCurrent(date);
			}
		});
	},

	/**
	 * 更新当前选中会话通知
	 * @param {Object} date - 消息数据
	 * @private
	 */
	_updateCurrent(date) {
		const that = globalState;

		// 重置记录当前会话框 用户发送消息数量 + 和客服发送消息数量
		that.unexpected_records = 0;
		// 清空聊天记录
		that.sessionMessagesList = [];

		if (date.session_type == 0) {
			// 私聊 清空当前选中会话的未读消息数量
			if (date.is_top == 1) {
				// 置顶会话
				const allDisturbItem = that.sessionList.all_disturb[0].find((sub) => sub.id == date.id);
				if (allDisturbItem) {
					allDisturbItem.unread_msg_count = 0;
				}
			} else {
				// 非置顶会话
				const allDisturbItem = that.sessionList.all_disturb[1].find((sub) => sub.id == date.id);
				if (allDisturbItem) {
					allDisturbItem.unread_msg_count = 0;
				}
			}
			// 私聊 - 清空当前选中会话的未读消息数量
			const privateItem = that.sessionList.private.find((sub) => sub.id == date.id);
			if (privateItem) {
				privateItem.unread_msg_count = 0;
			}
		} else {
			// 群聊
			if (date.is_top == 1) {
				// 置顶会话
				const allDisturbItem = that.sessionList.all_disturb[0].find((sub) => sub.id == date.id);
				if (allDisturbItem) {
					allDisturbItem.unread_msg_count = 0;
				}
			} else {
				// 非置顶会话
				const allDisturbItem = that.sessionList.all_disturb[1].find((sub) => sub.id == date.id);
				if (allDisturbItem) {
					allDisturbItem.unread_msg_count = 0;
				}
			}
			// 免打扰 - 清空当前选中会话的未读消息数量
			const allNotDisturbItem = that.sessionList.all_not_disturb.find((sub) => sub.id == date.id);
			if (allNotDisturbItem) {
				allNotDisturbItem.unread_msg_count = 0;
			}
			// 群聊 - 清空当前选中会话的未读消息数量
			const groupItem = that.sessionList.group.find((sub) => sub.id == date.id);
			if (groupItem) {
				groupItem.unread_msg_count = 0;
			}
		}

		// 获取聊天记录
		that.adminSocket.send({
			a: 'chatRecord',
			c: 'Message',
			data: {
				page: 1,
				session_id: that.currentSession.id
			}
		});

		// 获取会话详情
		that.adminSocket.send({
			a: 'sessionDetail',
			c: 'SessionManager',
			data: {
				session_id: that.currentSession.id
			}
		});

		// 获取商品列表
		// that.adminSocket.send({
		// 	a: 'productList',
		// 	c: 'Message',
		// 	data: {
		// 		session_user: that.currentSession.session_user,
		// 		page: 1
		// 	}
		// });

		setTimeout(() => {
			if (that.chatMessages) {
				that.chatMessages.scrollTop = that.chatMessages.scrollHeight;
			}
		}, 100);
	},

	/**
	 * 追加消息到列表
	 * @param {Object} date - 消息数据
	 * @private
	 */
	_appendMessageToList(date) {
		const that = globalState;
		that.sessionMessagesList = [
			...that.sessionMessagesList,
			{
				avatar: date.avatar || '',
				createtime: date.createtime || '',
				createtime_text: date.createtime_text || '',
				id: date.record_id || '',
				message: date.message || '',
				message_type: date.message_type,
				name: date.nickname || '',
				sender: date.sender,
				sender_id: date.sender_id || '',
				sender_identity: date.sender_identity,
				session_id: date.session_id || '',
				status: 1
			}
		];
	},

	/**
	 * 发送已读消息请求
	 * @param {Object} date - 消息数据
	 * @private
	 */
	_sendReadMessageRequest(date) {
		const that = globalState;
		that.adminSocket.send({
			c: 'Message',
			a: 'readMessage',
			data: {
				record_id: date.record_id,
				session_id: that.currentSession.id
			}
		});
	},

	/**
	 * 更新当前选中会话列表和消息
	 * @param {Object} date - 消息数据
	 * @private
	 */
	_updateCurrentSessionList(date) {
		const that = globalState;
		// 会话到顶部
		const moveSessionToTop = (list, item) => {
			const index = list.findIndex((session) => session.id == item.id);
			if (index != -1) {
				list.splice(index, 1);
				list.unshift(item);
			}
		};

		// 更新会话列表（最新消息、时间、未读数、会话类型）
		const updateList = (list) =>
			list.map((item) =>
				item.id == date.id
					? {
							...item,
							message_type: date.message_type,
							last_message: date.last_message || '',
							last_time: date.last_time,
							last_timestamp: date.createtime,
							unread_msg_count: 0,
							session_type: date.session_type
					  }
					: item
			);
		if (that.currentSession.session_type == 0) {
			// 私聊：更新私聊列表
			that.sessionList.private = updateList(that.sessionList.private);
			// 移动顶部
			moveSessionToTop(
				that.sessionList.private,
				that.sessionList.private.find((item) => item.id == that.currentSession.id)
			);
			// 当前会话有没有设置置顶
			if (that.currentSession.is_top == 1) {
				// 设置了置顶
				that.sessionList.all_disturb[0] = updateList(that.sessionList.all_disturb[0]);
				moveSessionToTop(
					that.sessionList.all_disturb[0],
					that.sessionList.all_disturb[0].find((item) => item.id == that.currentSession.id)
				);
			} else {
				// 没有设置置顶
				that.sessionList.all_disturb[1] = updateList(that.sessionList.all_disturb[1]);
				moveSessionToTop(
					that.sessionList.all_disturb[1],
					that.sessionList.all_disturb[1].find((item) => item.id == that.currentSession.id)
				);
			}
		} else {
			// 当前会话有没有设置免打扰
			if (date.is_do_not_disturb == 1) {
				// 设置了免打扰
				that.sessionList.all_not_disturb = updateList(that.sessionList.all_not_disturb);
				moveSessionToTop(
					that.sessionList.all_not_disturb,
					that.sessionList.all_not_disturb.find((item) => item.id == that.currentSession.id)
				);
			} else {
				// 没有设置免打扰
				if (that.currentSession.is_top == 1) {
					// 设置了置顶
					that.sessionList.all_disturb[0] = updateList(that.sessionList.all_disturb[0]);
					moveSessionToTop(
						that.sessionList.all_disturb[0],
						that.sessionList.all_disturb[0].find((item) => item.id == that.currentSession.id)
					);
				} else {
					// 没有设置置顶
					that.sessionList.all_disturb[1] = updateList(that.sessionList.all_disturb[1]);
					moveSessionToTop(
						that.sessionList.all_disturb[1],
						that.sessionList.all_disturb[1].find((item) => item.id == that.currentSession.id)
					);
				}
				// 群聊：更新群聊列表和已设置免打扰列表
				that.sessionList.group = updateList(that.sessionList.group);
				moveSessionToTop(
					that.sessionList.group,
					that.sessionList.group.find((item) => item.id == that.currentSession.id)
				);
			}
		}
	},

	/**
	 * 更新没有选中会话列表
	 * @param {Object} date - 消息数据
	 * @private
	 */
	_updateSessionList(date) {
		const that = globalState;
		// 会话到顶部
		const moveSessionToTop = (list, item) => {
			const index = list.findIndex((session) => session.id == item.id);
			if (index != -1) {
				list.splice(index, 1);
				list.unshift(item);
			}
		};

		// 更新会话列表（最新消息、时间、未读数、会话类型）
		const updateList = (list) =>
			list.map((item) =>
				item.id == date.id
					? {
							...item,
							message_type: date.message_type,
							last_message: date.last_message || '',
							last_time: date.last_time,
							last_timestamp: date.createtime,
							unread_msg_count: date.unread_msg_count,
							session_type: date.session_type,
							session_name: date.session_name,
							session_session_members: date.session_session_members
					  }
					: item
			);

		if (date.session_type == 0) {
			// 私聊
			const privateItem = that.sessionList.private.find((item) => item.id == date.id);
			if (privateItem) {
				// 私聊：更新私聊列表
				that.sessionList.private = updateList(that.sessionList.private);
				// 移动顶部
				moveSessionToTop(
					that.sessionList.private,
					that.sessionList.private.find((item) => item.id == date.id)
				);
				if (date.is_top == 1) {
					// 设置了置顶
					that.sessionList.all_disturb[0] = updateList(that.sessionList.all_disturb[0]);
					moveSessionToTop(
						that.sessionList.all_disturb[0],
						that.sessionList.all_disturb[0].find((item) => item.id == date.id)
					);
				} else {
					// 没有设置置顶
					that.sessionList.all_disturb[1] = updateList(that.sessionList.all_disturb[1]);
					moveSessionToTop(
						that.sessionList.all_disturb[1],
						that.sessionList.all_disturb[1].find((item) => item.id == date.id)
					);
				}
			} else {
				const buildItem = () => ({
					avatar: date.avatar || '',
					csr_id: '',
					id: date.id,
					is_top: date.is_top,
					is_do_not_disturb: 0,
					last_message: date.last_message || '',
					last_time: date.last_time || '',
					message_type: date.message_type,
					last_timestamp: date.createtime || '',
					nickname: date.nickname || '',
					online: date.online,
					session_name: date.session_name || '',
					session_user: date.session_user || '',
					session_type: date.session_type,
					unread_msg_count: date.unread_msg_count || 0,
					user_id: ''
				});
				that.sessionList.private.unshift(buildItem());
				that.sessionList.all_disturb[1].unshift(buildItem());
			}
		} else {
			// 群聊
			const buildItem = () => ({
				avatar: date.avatar || '',
				csr_id: '',
				id: date.id,
				is_top: date.is_top,
				is_do_not_disturb: 0,
				last_message: date.last_message || '',
				last_time: date.last_time || '',
				message_type: date.message_type,
				last_timestamp: date.createtime || '',
				nickname: date.nickname || '',
				online: date.online,
				session_name: date.session_name || '',
				session_user: date.session_user || '',
				session_members: date.session_members || '',
				session_type: date.session_type,
				unread_msg_count: date.unread_msg_count || 0,
				user_id: ''
			});
			// 私聊中有同 id 则删除当前私聊，添加为群聊会话
			const privateItem = that.sessionList.private.find((item) => item.id == date.id);
			if (privateItem) {
				// 删除私聊里面的会话
				that.sessionList.private.splice(that.sessionList.private.indexOf(privateItem), 1);
				if (date.is_top == 1) {
					// 设置了置顶
					that.sessionList.all_disturb[0].splice(that.sessionList.all_disturb[0].indexOf(privateItem), 1);
				} else {
					// 没有设置置顶
					that.sessionList.all_disturb[1].splice(that.sessionList.all_disturb[1].indexOf(privateItem), 1);
				}

				// 添加到群聊列表
				that.sessionList.group.unshift(buildItem());
				// 添加到全部（未设置置顶）列表
				that.sessionList.all_disturb[1].unshift(buildItem());
				return;
			}
			// 群聊中有同 id 则更新并移顶
			const groupItem = that.sessionList.group.find((item) => item.id == date.id);
			if (groupItem) {
				that.sessionList.group = updateList(that.sessionList.group);
				// 移动顶部
				moveSessionToTop(
					that.sessionList.group,
					that.sessionList.group.find((item) => item.id == date.id)
				);

				if (date.is_do_not_disturb == 1) {
					// 设置了免打扰
					that.sessionList.all_not_disturb = updateList(that.sessionList.all_not_disturb);
				} else {
					// 没有设置免打扰
					if (date.is_top == 1) {
						// 设置了置顶
						that.sessionList.all_disturb[0] = updateList(that.sessionList.all_disturb[0]);
						// 移动顶部
						moveSessionToTop(
							that.sessionList.all_disturb[0],
							that.sessionList.all_disturb[0].find((item) => item.id == date.id)
						);
					} else {
						// 没有设置置顶
						that.sessionList.all_disturb[1] = updateList(that.sessionList.all_disturb[1]);
						// 移动顶部
						moveSessionToTop(
							that.sessionList.all_disturb[1],
							that.sessionList.all_disturb[1].find((item) => item.id == date.id)
						);
					}
				}
				return;
			}
			// 添加到群聊列表
			that.sessionList.group.unshift(buildItem());
			// 添加到全部（未设置免打扰）列表
			that.sessionList.all_disturb[1].unshift(buildItem());
		}
	},

	/**
	 * 自动滚动到底部
	 * @private
	 */
	_autoScrollToBottom() {
		const that = globalState;
		this.$nextTick(() => {
			if (!that.chatMessages) return;
			let lastMsg;
			const messages = that.chatMessages.querySelectorAll('.message-item, .message-system');
			lastMsg = messages.length > 0 ? messages[messages.length - 1] : null;

			if (lastMsg) {
				const isAtBottom = that.chatMessages.scrollTop + that.chatMessages.clientHeight >= that.chatMessages.scrollHeight - (lastMsg.clientHeight + 50);
				if (isAtBottom) that.chatMessages.scrollTop = that.chatMessages.scrollHeight;
			}
		});
	},

	// 会话详情消息
	session_detail(data) {
		const that = globalState;
		const date = data.data;
		that.currentSessionInfo = {
			session_type: date.session_type || '', // 当前会话类型
			session_name: date.session_name || '', // 当前会话名称
			session_info: date.session_info || {}, // 私聊会话信息
			group_members: date.group_members || [], // 群聊会话群成员
			session_order_sn: date.session_order_sn || '', // 三角洲租号订单号
			session_game_id: date.session_game_id || '', // 游戏id
			// consult_goods: date.consult_goods || {}, // 咨询宝贝
			// recent_orders: date.recent_orders || {}, // 最近订单
			// footprints: date.footprints || {}, // 最近足迹
			// transfer_records: date.transfer_records || {}, // 转接记录
			// trajectory: date.trajectory.trajectory || [] // 轨迹
		};

        if (date.session_game_id == 20 && date.session_order_sn) {
            // 三角洲租号订单详情
            that.adminSocket.send({
                a: 'rentalOrderDetail',
                c: 'OrderManager',
                data: {
                    order_no: date.session_order_sn || ''
                }
            });
        } else {

            /**
             * 批量发送 Socket 消息
             * @param {Array} messages - 消息数组 [{ a, c, data }]
             */
            const batchSendSocket = (messages) => {

                messages.forEach(msg => {
                    that.adminSocket.send({ a: msg.a, c: msg.c, data: msg.data || {} })
                })
            }
            batchSendSocket([
                { a: 'productList', c: 'Message', data: { session_user: that.currentSession.session_user, page: 1 } },
                { a: 'recentOrders', c: 'OrderManager', data: { session_user: that.currentSession.session_user } },
                { a: 'transferRecords', c: 'UserManager', data: { session_user: that.currentSession.session_user } },
                { a: 'trajectory', c: 'UserManager', data: { session_user: that.currentSession.session_user } },
                { a: 'footprints', c: 'UserManager', data: { session_user: that.currentSession.session_user } },
                { a: 'consultGoods', c: 'UserManager', data: { session_user: that.currentSession.session_user } }
            ])
        }
	},

    // 三角洲租号订单详情
    rental_order_detail(data) {
        const that = globalState;
        const date = data.data;
        that.currentSessionInfo = {
            ...that.currentSessionInfo,
            rental_order_detail: date || {}
        }
    },

    // 最近订单
    recent_orders(data) {
        const that = globalState;
        const date = data.data;
        that.currentSessionInfo = {
            ...that.currentSessionInfo,
            recent_orders: date.list || []
        }
    },

    // 转接记录
    transfer_records(data) {
        const that = globalState;
        const date = data.data;
        that.currentSessionInfo = {
            ...that.currentSessionInfo,
            transfer_records: date.list || []
        }
    },

    // 轨迹
    trajectory(data) {
        const that = globalState;
        const date = data.data;
        that.currentSessionInfo = {
            ...that.currentSessionInfo,
            trajectory: date.trajectory || []
        }
    },

    // 最近足迹
    footprints(data) {
        const that = globalState;
        const date = data.data;
        that.currentSessionInfo = {
            ...that.currentSessionInfo,
            footprints: date.list || []
        }
    },

    // 咨询宝贝
    consult_goods(data) {
        const that = globalState;
        const date = data.data;
        console.log('date', date);
        that.currentSessionInfo = {
            ...that.currentSessionInfo,
            consult_goods: date.list || []
        }
    },

	// 修改用户信息
	action_user(data) {
		const that = globalState;
		const date = data.data;
		// console.log('action_user', date);
		if (date.action == 'add_tag') {
			// 添加标签
			that.currentSessionInfo.session_info.tags.push({
				tag_name: date.tag.tag_name
			});
		} else if (date.action == 'delete_tag') {
			// 删除标签
			that.currentSessionInfo.session_info.tags = that.currentSessionInfo.session_info.tags.filter((item) => item.tag_name != date.tag_name);
		} else if (date.action == 'update_status') {
			// 关注状态
			that.currentSessionInfo.session_info.follow_status = date.follow_status;
		}
	},

	// 操作会话
	action_session(data) {
		const that = globalState;
		const date = data.data;
		// console.log('action_session', date);
		if (date.action == 'create_group') {
			// 建群-获取客服列表
			that.csrList = date.csr_list;
		} else if (date.action == 'create_group_done') {
			// 建群-完成
			this._handleCreateGroupDone(date);
		} else if (date.action == 'transfer') {
			// 转接-获取客服列表
			that.csrList = date.csr_list;
		} else if (date.action == 'transfer_done') {
			// 转接-完成
			this._handleTransferDone(date);
		} else if (date.action == 'top') {
			// 置顶
			this._handleTop(date);
		} else if (date.action == 'do_not_disturb') {
			// 免打扰
			this._handleDoNotDisturb(date);
		} else if (date.action == 'edit_sessionname') {
			// 修改群聊昵称
			this._handleSessionName(date);
		}
	},

    // 拉黑
    blacklist(data) {
        const that = globalState;
        const date = data.data;
        // console.log('blacklist', date);
        // 获取会话详情
		that.adminSocket.send({
			a: 'sessionDetail',
			c: 'Message',
			data: {
				session_id: that.currentSession.id
			}
		});
    },

	// ── 操作会话工具方法 ──────────────────────────────────────────────────────────
	/**
	 * 处理建群完成
	 * @param {Object} date - 建群数据
	 * @private
	 */
	_handleCreateGroupDone(date) {
		const that = globalState;
		// 1、当前类型改为群聊
		let currentSession;
		if (that.currentSession.is_top == 1) {
			currentSession = that.sessionList.all_disturb[0].find((item) => item.id == that.currentSession.id);
		} else {
			currentSession = that.sessionList.all_disturb[1].find((item) => item.id == that.currentSession.id);
		}
		if (currentSession) {
			currentSession.session_type = 1;
			currentSession.session_name = date.session_name;
			currentSession.session_members = date.session_members;
			that.currentSession.session_type = 1;

			// 2、从私聊中移除这个会话
			that.sessionList.private.splice(
				that.sessionList.private.findIndex((item) => item.id == currentSession.id),
				1
			);

			const groupSessionIndex = that.sessionList.group.find((item) => item.id == that.currentSession.id);
			if (groupSessionIndex) {
				// 3、群聊中已存在这个会话，更新会话信息
				groupSessionIndex.session_type = 1;
				groupSessionIndex.session_name = date.session_name;
				groupSessionIndex.session_members = date.session_members;
			} else {
				// 4、群聊中没有这个会话，添加群聊会话
				that.sessionList.group.unshift(currentSession);
			}
		}
	},

	/**
	 * 处理转接完成
	 * @param {Object} date - 转接数据
	 * @private
	 */
	_handleTransferDone(date) {
		const that = globalState;
		// 1、更新会话信息
		const session = that.sessionList.private.find((item) => item.id == date.session_id);
		if (session) {
			if (session.is_top == 1) {
				that.sessionList.all_disturb[0].splice(
					that.sessionList.all_disturb[0].findIndex((item) => item.id == session.id),
					1
				);
			} else {
				that.sessionList.all_disturb[1].splice(
					that.sessionList.all_disturb[1].findIndex((item) => item.id == session.id),
					1
				);
			}
			that.sessionList.private.splice(
				that.sessionList.private.findIndex((item) => item.id == session.id),
				1
			);
		}
		that.currentSession = {};
		that.currentSessionInfo = {};
		that.sessionMessagesList = [];
	},

	/**
	 * 处理置顶
	 * @param {Object} date - 置顶数据
	 * @private
	 */
	_handleTop(date) {
		const that = globalState;
		// 获取当前的两个列表
		let topList = [...that.sessionList.all_disturb[0]]; // 置顶列表
		let normalList = [...that.sessionList.all_disturb[1]]; // 未置顶列表

		if (date.is_top == 1) {
			// 从未置顶移到置顶
			const itemIndex = normalList.findIndex((item) => item.id == date.session_id);
			if (itemIndex !== -1) {
				const item = { ...normalList[itemIndex], is_top: 1 };
				normalList.splice(itemIndex, 1); // 从原列表删除
				topList.unshift(item); // 添加到置顶列表开头
			}
		} else {
			// 从置顶移到未置顶
			const itemIndex = topList.findIndex((item) => item.id == date.session_id);
			if (itemIndex !== -1) {
				const item = { ...topList[itemIndex], is_top: 0 };
				topList.splice(itemIndex, 1); // 从置顶列表删除
				normalList.unshift(item); // 添加到未置顶列表开头
			}
		}
		this.$set(that.sessionList.all_disturb, 0, topList);
		this.$set(that.sessionList.all_disturb, 1, normalList);
	},

	/**
	 * 处理免打扰
	 * @param {Object} date - 免打扰数据
	 * @private
	 */
	_handleDoNotDisturb(date) {
		const that = globalState;
		const updateList = (list) =>
			list.map((item) =>
				item.id == date.session_id
					? {
							...item,
							is_do_not_disturb: date.is_do_not_disturb
					  }
					: item
			);

		that.sessionList.all_disturb[0] = updateList(that.sessionList.all_disturb[0]);
		that.sessionList.all_disturb[1] = updateList(that.sessionList.all_disturb[1]);
		that.sessionList.all_not_disturb = updateList(that.sessionList.all_not_disturb);

		// 取消免打扰，从免打扰列表中移除这个会话
		const item = that.sessionList.all_not_disturb.find((item) => item.id == date.session_id);
		if (item) {
			that.sessionList.all_not_disturb.splice(that.sessionList.all_not_disturb.indexOf(item), 1);
			if (item.is_top == 1) {
				that.sessionList.all_disturb[0].unshift(item);
			} else {
				that.sessionList.all_disturb[1].unshift(item);
			}

			return;
		}

		// 从置顶列表或者未置顶列表中移除这个会话，添加到免打扰列表
		const topItem = that.sessionList.all_disturb[0].find((item) => item.id == date.session_id);
		if (topItem) {
			that.sessionList.all_disturb[0].splice(that.sessionList.all_disturb[0].indexOf(topItem), 1);
			that.sessionList.all_not_disturb.unshift(topItem);
		} else {
			const normalItem = that.sessionList.all_disturb[1].find((item) => item.id == date.session_id);
			if (normalItem) {
				that.sessionList.all_disturb[1].splice(that.sessionList.all_disturb[1].indexOf(normalItem), 1);
				that.sessionList.all_not_disturb.unshift(normalItem);
			}
		}
	},

	/**
	 * 处理会话名称
	 * @param {Object} date - 免打扰数据
	 * @private
	 */
	_handleSessionName(date) {
	    const that = globalState;
		that.sessionList.group.forEach((item) => {
			if (item.id == date.session_id) {
				item.session_name = date.new_sessionname;
			}
		})
		that.sessionList.all_disturb[0].forEach((item) => {
			if (item.id == date.session_id) {
				item.session_name = date.new_sessionname;
			}
		})
		that.sessionList.all_disturb[1].forEach((item) => {
			if (item.id == date.session_id) {
				item.session_name = date.new_sessionname;
			}
		})

		// 获取会话详情
		that.adminSocket.send({
			a: 'sessionDetail',
			c: 'Message',
			data: {
				session_id: that.currentSession.id
			}
		});
	},

	/** 注册聊天记录滚动容器 DOM */
	setChatMessages(chatMessages) {
		globalState.chatMessages = chatMessages;
	},

	/** 注册编辑器 DOM 引用 */
	setEditorRef(editorRef) {
		globalState.editorRef = editorRef;
	},

	// ── 工具方法 ──────────────────────────────────────────────────────────
	/**
	 * 生成本地临时消息 ID（时间戳 + 随机数）
	 * 服务端确认后会返回真实 id 替换
	 * @returns {number}
	 */
	generateMessageId() {
		return Date.now() + 246 + Math.floor(Math.random() * 10000);
	},

    // 商品字段拼接
    buildProductInfo(item, message_type) {
        const productInfoParts = [`message_type=${message_type}`];
        for (const key in item) {
            if (!Object.hasOwn(item, key)) continue;
            
            const element = item[key];
            // console.log('element', key + ':' + ' ' + element)
            productInfoParts.push(`${key}=${element}`);
        }

        // console.log('productInfoParts', productInfoParts.join('#'));
        
        return productInfoParts.join('#');
    },

    // 商品字段解析
    getProductInfo(item) {
        // 将字符串按 `#` 分割成一个数组
        const pairs = item.message.split('#');

        // 使用 reduce 将数组转成对象
        const result = pairs.reduce((acc, pair) => {
            const [key, value] = pair.split('=');
            acc[key] = value;
            return acc;
        }, {});

        return result;
    },

    // 处理图片
    handleImage(imgs) {
        if (!imgs) return '';
        return imgs.split(',')[0];
    },

	/** 设置右侧面板当前 tab */
	setRelatedRecordsTab(tabId) {
		globalState.activeRelatedTab = tabId;
	},
};

// ─── 默认导出（组合对象，方便组件一次性引入）────────────────────────────────

export default {
	state: globalState,
	methods: globalMethods,
	ui
};
